import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { XIcon } from "@heroicons/react/outline";

import { Button } from "../../button";
import { Input } from "../../input";
import { useSearchOptional } from "../Search/search-store";

import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { ReportColumn } from "@/types/evaluate";

interface FilterInputProps {
  column: ReportColumn;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const FilterInput = observer(({ column, isOpen, setIsOpen }: FilterInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const searchStore = useSearchOptional();
  const columnId = column.id?.toString() || "";

  const [inputValue, setInputValue] = useState(searchStore?.filters[columnId]?.[0] || "");

  useEffect(() => {
    if (isOpen) {
      setInputValue(searchStore?.filters[columnId]?.[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const applyFilter = useCallback(() => {
    if (inputValue !== "") {
      searchStore?.updateFilter(columnId, inputValue);
    }
    setInputValue("");
    setIsOpen(false);
  }, [columnId, inputValue, searchStore, setIsOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        applyFilter();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setInputValue("");
        setIsOpen(false);
      }
    },
    [applyFilter, setIsOpen],
  );

  const handleBlur = useCallback(() => {
    if (!inputValue) {
      searchStore?.removeFilter(columnId, inputValue);
    }
  }, [columnId, inputValue, searchStore]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverAnchor className="absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2" />
      <PopoverContent className="mr-2 w-96 p-4" onClick={e => e.stopPropagation()}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filter "{column.name}"</span>
          </div>
          <div className="text-xs text-gray-500">Show rows where this column contains:</div>
          <div className="relative">
            <Input
              type="text"
              key="filter-input"
              tabIndex={-1}
              ref={inputRef}
              onBlur={handleBlur}
              value={inputValue}
              onChange={handleFilterChange}
              onKeyDown={e => {
                e.stopPropagation();
                handleKeyDown(e);
              }}
              placeholder="Enter filter value..."
              className="w-full pr-8 text-sm"
            />
            {inputValue && (
              <Button
                onClick={() => {
                  setInputValue("");
                  searchStore?.removeFilter(columnId, inputValue);
                }}
                size="tinyIcon"
                className="absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2 hover:bg-gray-100"
                variant="ghost"
              >
                <XIcon className="h-3 w-3 text-gray-400 hover:text-red-500" />
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-gray-400">Press Enter to apply or Esc to close</div>
            <Button
              onClick={applyFilter}
              size="sm"
              className="px-4 py-1.5 text-xs font-medium"
              disabled={!inputValue.trim()}
            >
              Apply Filter
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

export default FilterInput;
